<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Domains</h1>
        <p class="text-gray-600">Manage your sending domains and monitor reputation scores</p>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="syncFromMailgun"
          :disabled="syncingDomains"
          class="btn-secondary"
        >
          <span v-if="syncingDomains" class="flex items-center">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
            Syncing...
          </span>
          <span v-else class="flex items-center">
            <ArrowPathIcon class="w-5 h-5 mr-2" />
            Sync from Mailgun
          </span>
        </button>
        <button
          @click="showCreateModal = true"
          class="btn-primary"
        >
          <PlusIcon class="w-5 h-5 mr-2" />
          Add Domain
        </button>
      </div>
    </div>

    <!-- Domain Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <GlobeAltIcon class="h-8 w-8 text-primary-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Total Domains</p>
            <p class="text-2xl font-semibold text-gray-900">{{ domains.length }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <ChartBarIcon class="h-8 w-8 text-success-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Average Score</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ averageScore.toFixed(1) }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <ExclamationTriangleIcon class="h-8 w-8 text-warning-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Low Score Domains</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ lowScoreDomainsCount }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Domains List -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Domain Management</h2>
          <div class="flex items-center space-x-2">
            <button
              @click="refreshAllScores"
              :disabled="refreshingScores"
              class="btn-secondary"
            >
              <ArrowPathIcon class="w-4 h-4 mr-2" />
              Refresh Scores
            </button>
          </div>
        </div>
      </div>
      
      <div class="p-6">
        <div v-if="loading" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
        
        <div v-else-if="domains.length === 0" class="text-center py-8">
          <GlobeAltIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No domains yet</h3>
          <p class="mt-1 text-sm text-gray-500">Sync domains from Mailgun or add your first sending domain to get started.</p>
          <div class="mt-6 flex items-center justify-center space-x-3">
            <button
              @click="syncFromMailgun"
              :disabled="syncingDomains"
              class="btn-secondary"
            >
              <span v-if="syncingDomains" class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Syncing...
              </span>
              <span v-else class="flex items-center">
                <ArrowPathIcon class="w-4 h-4 mr-2" />
                Sync from Mailgun
              </span>
            </button>
            <button
              @click="showCreateModal = true"
              class="btn-primary"
            >
              <PlusIcon class="w-4 h-4 mr-2" />
              Add Domain
            </button>
          </div>
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="domain in sortedDomains"
            :key="domain.DomainID"
            class="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors duration-200"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <div
                    class="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                    :class="getScoreColorClass(domain.CalculatedScore)"
                  >
                    {{ domain.CalculatedScore }}
                  </div>
                </div>
                <div>
                  <h3 class="text-lg font-medium text-gray-900">{{ domain.DomainName }}</h3>
                  <div class="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                    <span>Score: {{ domain.CalculatedScore }}/100</span>
                    <span>•</span>
                    <span>Last updated: {{ formatDate(domain.LastScoreUpdate) }}</span>
                    <span v-if="domain.MailgunDomainID" class="text-primary-600">
                      • Mailgun Connected
                    </span>
                  </div>
                  <div class="mt-2">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getScoreBadgeClass(domain.CalculatedScore)"
                    >
                      {{ getScoreLabel(domain.CalculatedScore) }}
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center space-x-2">
                <button
                  @click="refreshDomainScore(domain)"
                  :disabled="refreshingScores"
                  class="btn-secondary"
                >
                  <ArrowPathIcon class="w-4 h-4 mr-2" />
                  Refresh
                </button>
                <button
                  @click="editDomain(domain)"
                  class="btn-primary"
                >
                  Edit
                </button>
                <button
                  @click="viewDomainStats(domain)"
                  class="btn-secondary"
                >
                  Stats
                </button>
                <button
                  @click="deleteDomain(domain.DomainID)"
                  class="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Domain Modal -->
    <DomainModal
      v-if="showCreateModal"
      :domain="editingDomain"
      @close="closeModal"
      @saved="onDomainSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { domainsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import {
  PlusIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/vue/24/outline'
import DomainModal from '../components/DomainModal.vue'

// State
const domains = ref([])
const loading = ref(false)
const refreshingScores = ref(false)
const syncingDomains = ref(false)
const showCreateModal = ref(false)
const editingDomain = ref(null)

// Computed
const sortedDomains = computed(() => {
  return [...domains.value].sort((a, b) => b.CalculatedScore - a.CalculatedScore)
})

const averageScore = computed(() => {
  if (domains.value.length === 0) return 0
  const total = domains.value.reduce((sum, domain) => sum + domain.CalculatedScore, 0)
  return total / domains.value.length
})

const lowScoreDomainsCount = computed(() => {
  return domains.value.filter(domain => domain.CalculatedScore < 70).length
})

// Methods
const getScoreColorClass = (score) => {
  if (score >= 90) return 'bg-success-600'
  if (score >= 80) return 'bg-success-500'
  if (score >= 70) return 'bg-warning-500'
  if (score >= 60) return 'bg-warning-600'
  return 'bg-red-600'
}

const getScoreBadgeClass = (score) => {
  if (score >= 90) return 'bg-success-100 text-success-800'
  if (score >= 80) return 'bg-success-100 text-success-800'
  if (score >= 70) return 'bg-warning-100 text-warning-800'
  if (score >= 60) return 'bg-warning-100 text-warning-800'
  return 'bg-red-100 text-red-800'
}

const getScoreLabel = (score) => {
  if (score >= 90) return 'Excellent'
  if (score >= 80) return 'Good'
  if (score >= 70) return 'Fair'
  if (score >= 60) return 'Poor'
  return 'Critical'
}

const formatDate = (dateString) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString()
}

const fetchDomains = async () => {
  try {
    loading.value = true
    const response = await domainsApi.getAll()
    domains.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching domains:', error)
    toast.error('Failed to fetch domains')
  } finally {
    loading.value = false
  }
}

const refreshDomainScore = async (domain) => {
  try {
    const response = await domainsApi.refreshScore(domain.DomainID)
    if (response.data.success) {
      toast.success(`Domain score refreshed: ${response.data.data.calculatedScore}`)
      await fetchDomains()
    }
  } catch (error) {
    console.error('Error refreshing domain score:', error)
    toast.error('Failed to refresh domain score')
  }
}

const refreshAllScores = async () => {
  try {
    refreshingScores.value = true
    const promises = domains.value.map(domain => refreshDomainScore(domain))
    await Promise.all(promises)
    toast.success('All domain scores refreshed')
  } catch (error) {
    console.error('Error refreshing all scores:', error)
    toast.error('Failed to refresh some domain scores')
  } finally {
    refreshingScores.value = false
  }
}

const editDomain = (domain) => {
  editingDomain.value = domain
  showCreateModal.value = true
}

const viewDomainStats = (domain) => {
  // This would open a modal or navigate to a stats page
  toast.info(`Stats for ${domain.DomainName} - Coming soon!`)
}

const deleteDomain = async (domainId) => {
  if (!confirm('Are you sure you want to delete this domain? This action cannot be undone.')) {
    return
  }

  try {
    await domainsApi.delete(domainId)
    toast.success('Domain deleted successfully')
    await fetchDomains()
  } catch (error) {
    console.error('Error deleting domain:', error)
    toast.error('Failed to delete domain')
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingDomain.value = null
}

const onDomainSaved = async () => {
  closeModal()
  await fetchDomains()
  toast.success('Domain saved successfully')
}

const syncFromMailgun = async () => {
  try {
    syncingDomains.value = true
    const response = await domainsApi.sync()
    if (response.data.success) {
      const { synced, created, updated, skipped } = response.data.data
      let message = `Synced ${synced} domain${synced !== 1 ? 's' : ''} from Mailgun`
      if (created > 0) message += ` (${created} new)`
      if (updated > 0) message += ` (${updated} updated)`
      if (skipped > 0) message += ` (${skipped} skipped)`
      toast.success(message)
      await fetchDomains()
    }
  } catch (error) {
    console.error('Error syncing domains:', error)
    // Error toast is handled by API interceptor
  } finally {
    syncingDomains.value = false
  }
}

// Lifecycle
onMounted(async () => {
  await fetchDomains()
})
</script> 